"use client";

import { useLanguage } from "../i18n/LanguageProvider";

/* ============================================================
   PITA MOTTO - #NyamanSepertiDiRumah
   Dipasang di app/tentang-kami/about/AboutContent.jsx

   >>> KENAPA BIDANG HIJAU PEKAT, BUKAN KARTU PUTIH <<<

   Halaman About sekarang berurutan: putih (profil) -> #f1faf5
   (fakta) -> slate-50 (Center of Excellence) -> putih (lokasi).
   Empat bidang terang berturut-turut. Menaruh motto sebagai kartu
   putih kelima membuatnya lewat begitu saja tanpa dilihat.

   Motto bukan informasi - ia pernyataan. Pernyataan butuh jeda, dan
   satu-satunya cara membuat jeda di halaman yang seluruhnya terang
   adalah dengan membalik nilainya. Ini SATU-SATUNYA bidang gelap di
   halaman, dan memang harus tetap satu-satunya: begitu ada dua, tidak
   ada lagi yang menonjol.

   ============================================================
   >>> TANDA PAGAR DIBUAT LEBIH REDUP DARI KATANYA <<<

   "#" ditulis dengan text-white/40, bukan putih penuh. Tanda pagar
   adalah penanda format, bukan bagian dari maknanya - kalau sama
   pekatnya dengan kata, mata membacanya lebih dulu dan kalimatnya
   jadi tersendat.

   ============================================================
   >>> MASALAH TEKNIS YANG SEBENARNYA: SATU KATA 21 HURUF <<<

   "#NyamanSepertiDiRumah" tidak punya spasi, jadi browser TIDAK BISA
   memenggalnya. Di layar 360px, ukuran font apa pun di atas ~30px
   akan menembus tepi layar dan memunculkan gulir horizontal di
   SELURUH halaman - bukan cuma di bagian ini.

   Karena itu ukurannya naik bertahap dan baru besar mulai md, dan
   ada `break-words` sebagai jaring pengaman. Jangan menaikkan ukuran
   dasarnya tanpa mengujinya di lebar 360px lebih dulu.

   ============================================================
   >>> PILAR MASIH MATI - LIHAT TAMPILKAN_PILAR <<<

   Tiga pilar di bawah motto sengaja TIDAK dirender. Isinya masih
   kalimat GANTI, dan satu-satunya fakta yang benar-benar saya punya
   soal kenyamanan (buka 24 jam, akreditasi KARS, berdiri 1993) sudah
   tampil utuh di strip Fakta beberapa ratus piksel di atas. Mengulang
   ketiganya di sini tidak menambah apa pun - ia justru membuat
   halaman terasa kehabisan bahan.

   Minta ke tim RS TIGA hal yang benar-benar membuat pasien merasa
   seperti di rumah - misalnya kebijakan penunggu pasien, ruang rawat
   yang bisa dipilih, atau jam besuk yang longgar. Tulis di TEKS.pilar,
   lalu ubah TAMPILKAN_PILAR jadi true. Jangan dikarang: kalimat
   kenyamanan yang tidak ditepati di lapangan adalah bahan keluhan
   yang paling sering dikutip pasien.

   ============================================================
   >>> JANGAN TULIS className SEBAGAI STRING MULTI-BARIS. <<<
   Proyek ini berakhiran baris CRLF. String class yang dipecah
   beberapa baris ikut membawa \r\n ke dalam nilai atribut dan memicu
   hydration error. Kalau perlu dipecah, pakai ["...", "..."].join(" ").
   ============================================================ */

const TAMPILKAN_PILAR = false;

const TEKS = {
  eyebrow: { id: "", en: "" },

  /* Satu kalimat, dan sengaja hanya satu. Motto yang diikuti tiga
     paragraf penjelasan berhenti terasa seperti motto. */
  sub: {
    id: "Kami merawat dengan cara yang kami harapkan untuk keluarga kami sendiri - dekat, tenang, dan tanpa jarak.",
    en: "We care for you the way we would want our own family cared for - close, calm, and without distance.",
  },

  /* GANTI ketiganya dengan komitmen nyata dari tim RS, lalu setel
     TAMPILKAN_PILAR = true. Judul maksimal 3 kata; keterangan satu
     baris. Lebih panjang dari itu dan pilarnya jadi paragraf. */
  pilar: [
    {
      ikon: "rumah",
      judul: { id: "GANTI: judul pilar 1", en: "REPLACE: pillar 1 title" },
      isi: { id: "GANTI: satu kalimat pendek.", en: "REPLACE: one short sentence." },
    },
    {
      ikon: "hati",
      judul: { id: "GANTI: judul pilar 2", en: "REPLACE: pillar 2 title" },
      isi: { id: "GANTI: satu kalimat pendek.", en: "REPLACE: one short sentence." },
    },
    {
      ikon: "jam",
      judul: { id: "GANTI: judul pilar 3", en: "REPLACE: pillar 3 title" },
      isi: { id: "GANTI: satu kalimat pendek.", en: "REPLACE: one short sentence." },
    },
  ],
};

function pilihTeks(nilai, lang) {
  return nilai[lang] || nilai.id;
}

/* Ikon digambar di sini, bukan diimpor dari lucide-react, supaya
   bidang ini tidak menambah paket apa pun. Ketiganya stroke-only
   dengan tebal yang sama - ikon isi dan ikon garis yang dicampur
   dalam satu baris selalu terlihat seperti dua set yang berbeda. */
const IKON = {
  rumah: "M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1v-8.5Z",
  hati: "M12 20s-7-4.4-7-9.2A3.8 3.8 0 0 1 12 8a3.8 3.8 0 0 1 7 2.8C19 15.6 12 20 12 20Z",
  jam: "M12 7v5l3 2M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18Z",
};

export default function MottoNyaman() {
  const { lang } = useLanguage();

  return (
    <section className="relative isolate overflow-hidden bg-[var(--c-brand)]">

      {/* LAPISAN CAHAYA.

         Bidang hijau rata selebar layar terbaca seperti blok warna
         mentah. Satu sorotan lembut di kiri atas memberinya kedalaman
         tanpa menambah elemen yang harus dilihat.

         pointer-events-none WAJIB - tanpa itu lapisan ini menutupi
         teks di bawahnya dan blokir seleksi teks. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_15%_0%,rgba(255,255,255,0.14),transparent_60%)]" />

      <div className="relative mx-auto max-w-[1600px] px-4 py-14 sm:px-6 sm:py-20 md:py-24 lg:px-10">

        {/* max-w-3xl + mx-auto: baris teks berhenti di sekitar 70
           karakter meski layarnya 1600px. Motto yang melar selebar
           monitor jadi sulit dibaca justru karena besarnya. */}
        <div className="mx-auto max-w-3xl text-center">

          <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60 sm:mb-5 sm:text-xs">
            {pilihTeks(TEKS.eyebrow, lang)}
          </p>

          {/* GARIS PENDEK DI ATAS MOTTO.
             Menandai awal blok tanpa perlu kotak atau bingkai. Warna
             aksen merek yang sama dipakai kartu Center of Excellence. */}
          <span aria-hidden="true" className="mx-auto mb-6 block h-[3px] w-12 rounded-full bg-[var(--c-red)] sm:mb-7 sm:w-14" />

          {/* break-words = jaring pengaman untuk kata 21 huruf tanpa
             spasi. Lihat catatan di kepala berkas. */}
          <p className="break-words text-[27px] font-bold leading-[1.15] tracking-tight text-white sm:text-4xl md:text-5xl lg:text-[56px]">
            <span aria-hidden="true" className="text-white/40">#</span>
            <span className="sr-only">Tagar </span>
            NyamanSepertiDiRumah
          </p>

          <p className="mx-auto mt-6 max-w-xl text-pretty text-[15px] leading-relaxed text-white/85 sm:mt-7 sm:text-base md:text-[17px]">
            {pilihTeks(TEKS.sub, lang)}
          </p>

        </div>

        {/* ---------- PILAR ----------
           Dipisah GARIS, bukan kotak. Kartu berlatar di atas bidang
           hijau pekat selalu jadi keruh - entah terlalu mirip
           latarnya sehingga tidak terlihat, atau terlalu terang
           sehingga merebut perhatian dari mottonya. Garis rambut
           menyelesaikan keduanya. */}
        {TAMPILKAN_PILAR && (
          <ul className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-8 border-t border-white/15 pt-10 sm:mt-14 sm:gap-10 md:grid-cols-3 md:gap-0">
            {TEKS.pilar.map((p, i) => (
              <li key={p.ikon} className={i > 0 ? "md:border-l md:border-white/15 md:px-8" : "md:pr-8"}>
                <span aria-hidden="true" className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20">
                  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-white">
                    <path d={IKON[p.ikon]} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>

                <h3 className="mb-1.5 text-base font-bold leading-snug text-white sm:text-[17px]">
                  {pilihTeks(p.judul, lang)}
                </h3>

                {/* white/75, bukan white/60. Di atas hijau pekat,
                   white/60 hanya mencapai sekitar 4,1:1 - di bawah
                   ambang 4,5:1 untuk teks seukuran ini. */}
                <p className="text-sm leading-relaxed text-white/75">
                  {pilihTeks(p.isi, lang)}
                </p>
              </li>
            ))}
          </ul>
        )}

      </div>
    </section>
  );
}
